import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statisticsAPI } from '../api/axios';
import {
  Calendar,
  Users,
  FileText,
  Search,
  BarChart3,
  Scissors,
  CheckCircle,
  Clock,
  UserPlus
} from 'lucide-react';
import './AdminPage.css';

/**
 * 관리자 대시보드 페이지 (본문 컨텐츠만)
 * 사이드바와 헤더는 AdminLayout에서 제공
 */
function AdminPage() {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  // 통계 데이터 가져오기
  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await statisticsAPI.getTodaySummary();
      setStatistics(response.data);
    } catch (error) {
      console.error('통계 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 오늘 예약 카드 클릭 시 예약 관리 페이지의 오늘 뷰로 이동
  const handleTodayReservationsClick = () => {
    const today = new Date().toISOString().split('T')[0];
    navigate(`/admin/reservations?date=${today}`);
  };

  return (
    <div className="dashboard-content">
      {/* Welcome Card */}
      <div className="welcome-card">
        <h2 className="welcome-title">환영합니다, 원장님! 👋</h2>
        <p className="welcome-message">
          오늘도 고객님들을 위한 최고의 서비스를 준비해주세요.<br />
          ANEW와 함께 성공적인 하루 되시길 바랍니다.
        </p>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="loading-message">통계를 불러오는 중...</div>
      ) : statistics ? (
        <div className="stats-grid">
          <div 
            className="stat-card clickable"
            onClick={handleTodayReservationsClick}
          >
            <div className="stat-icon-wrapper">
              <Calendar className="stat-icon-lucide" size={28} />
            </div>
            <div className="stat-info">
              <p className="stat-label">오늘 예약</p>
              <p className="stat-value">{statistics.todayReservations}건</p>
            </div>
          </div>

          <div 
            className="stat-card"
            onClick={() => navigate('/admin/customers')}
          >
            <div className="stat-icon-wrapper">
              <Users className="stat-icon-lucide" size={28} />
            </div>
            <div className="stat-info">
              <p className="stat-label">전체 고객</p>
              <p className="stat-value">{statistics.totalCustomers}명</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrapper">
              <Clock className="stat-icon-lucide" size={28} />
            </div>
            <div className="stat-info">
              <p className="stat-label">대기 중</p>
              <p className="stat-value">{statistics.pendingReservations}건</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrapper">
              <CheckCircle className="stat-icon-lucide" size={28} />
            </div>
            <div className="stat-info">
              <p className="stat-label">완료</p>
              <p className="stat-value">{statistics.completedToday}건</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="error-message">통계 데이터를 불러올 수 없습니다.</div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3 className="section-title">빠른 작업</h3>
        <div className="actions-grid">
          <button 
            className="action-button"
            onClick={() => navigate('/admin/reservations')}
          >
            <FileText className="action-icon-lucide" size={32} />
            <span className="action-text">새 예약 등록</span>
          </button>

          <button 
            className="action-button"
            onClick={() => navigate('/admin/customers')}
          >
            <Search className="action-icon-lucide" size={32} />
            <span className="action-text">고객 조회</span>
          </button>

          <button 
            className="action-button"
            onClick={() => navigate('/admin/dashboard')}
          >
            <BarChart3 className="action-icon-lucide" size={32} />
            <span className="action-text">매출 대시보드</span>
          </button>

          <button
            className="action-button"
            onClick={() => navigate('/admin/treatments')}
          >
            <Scissors className="action-icon-lucide" size={32} />
            <span className="action-text">시술 관리</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <h3 className="section-title">최근 활동</h3>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-icon-wrapper">
              <CheckCircle className="activity-icon-lucide" size={24} />
            </div>
            <div className="activity-info">
              <p className="activity-text">김민지님의 피부관리 예약이 완료되었습니다.</p>
              <p className="activity-time">10분 전</p>
            </div>
          </div>

          <div className="activity-item">
            <div className="activity-icon-wrapper">
              <Calendar className="activity-icon-lucide" size={24} />
            </div>
            <div className="activity-info">
              <p className="activity-text">박서연님이 딥클렌징 예약을 신청했습니다.</p>
              <p className="activity-time">1시간 전</p>
            </div>
          </div>

          <div className="activity-item">
            <div className="activity-icon-wrapper">
              <UserPlus className="activity-icon-lucide" size={24} />
            </div>
            <div className="activity-info">
              <p className="activity-text">신규 고객 이지은님이 등록되었습니다.</p>
              <p className="activity-time">2시간 전</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
